import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useTranscriptionsState } from '@modules/transcriptions/useTranscriptionsState';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { Note } from 'src/@types/notes-transcription';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { useAuthContext } from 'src/auth/useAuthContext';
import { VerticalDivider } from 'src/components/VerticalDivider/VerticalDivider';
import { Iconify } from 'src/components/iconify';
import { HeaderIcon } from 'src/layouts/DashboardLayout/shared/styles';
import { useLocales } from 'src/locales';
import DocContainer, {
  pageStyle,
} from 'src/pages/dashboard/DocsPage/DocContent/DocContainer';
import {
  DocContentContextProvider,
  useDocContentContext,
} from 'src/pages/dashboard/DocsPage/DocContent/DocContentContext';
import { DocContentEditor } from 'src/pages/dashboard/DocsPage/DocContent/DocContentEditor';
import { DocContentNav } from 'src/pages/dashboard/DocsPage/DocContent/DocContentNav';
import { DocCover } from 'src/pages/dashboard/DocsPage/DocContent/DocCover';
import { DocTitle } from 'src/pages/dashboard/DocsPage/DocContent/DocTitle';
import { DocOptionsMenu } from 'src/pages/dashboard/DocsPage/DocContent/Header/HeaderActions/DocOptionsMenu';
import { FavoriteDocHeaderAction } from 'src/pages/dashboard/DocsPage/DocContent/Header/HeaderActions/FavoriteDocButton';
import { PinDocHeaderAction } from 'src/pages/dashboard/DocsPage/DocContent/Header/HeaderActions/PinDocButton';
import { ShareDocButton } from 'src/pages/dashboard/DocsPage/DocContent/Header/HeaderActions/ShareDocButton';
import { SharedWithYou } from 'src/pages/dashboard/DocsPage/DocContent/Header/HeaderActions/SharedWithYou';
import { useDocQuery } from 'src/pages/dashboard/DocsPage/shared/useDocQuery';
import { useUpdateDocMutation } from 'src/pages/dashboard/DocsPage/shared/useUpdateDocMutation';
import { DocsRequests } from 'src/requests/docs.js';
import { onDocsRefetch } from 'src/socket/listeners/docs.js';
import { useSocketEvent } from 'src/socket/use-event.js';
import { useDocsUIState } from 'src/state/ui/useDocsUIState';
import { createGlobalStyle } from 'styled-components';
import { PastSessionItemHeader } from '../../../GatheringSessions/PastSessionItemHeader';
import { useGatheringSessionByIdQuery } from '../../../GatheringSessions/useGatheringSessionByIdQuery';
import { WorkspaceShareMembersAvatar } from '../../../workspace/WorkspaceShareMembersAvatar';
import { RightMenuContent, RightMenuHeader } from '../../shared/styles';
import { useNotesTranscriptionModal } from '../TranscriptionModal';
import { AiTranscription } from '../index';
import { useTranscriptionQuery } from './useTranscriptionQuery';

const GlobalStyle = createGlobalStyle`
  @media print {
    .no-print {
      display: none !important;
    }
  }
`;

const EmptyArea = styled(Box)(() => ({
  minHeight: '30vh',
  width: '100%',
}));

export const TranscriptionRightMenuInner = ({
  onClose,
}: { onClose(): void }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const [openedTranscription, setOpenedTranscription] = useState(false);

  const { refetch } = useDocQuery();

  const navigate = useNavigate();

  const { translate } = useLocales();

  const { user } = useAuthContext();

  const theme = useTheme();

  const { updateDoc } = useUpdateDocMutation();

  const { doc, editor } = useDocContentContext();

  const { docsFullWidth } = useDocsUIState(doc._id);

  const { showNotesTranscriptionModal } = useNotesTranscriptionModal();

  const { transcription } = useTranscriptionQuery({
    transcriptionId: doc.noteRef as string,
    noteId: doc._id,
  });

  const { session } = useGatheringSessionByIdQuery({
    sessionId: (doc as Note).channelSession,
  });

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle,
  });

  const handleAddToEditor = (text: string) => {
    editor
      .chain()
      .focus('end')
      .splitBlock()
      .createParagraphNear()
      .insertContent(text)
      .run();
  };

  const goToDoc = () => {
    refetch();
    navigate(PATH_DASHBOARD.docs.view(doc._id));
    if (onClose) onClose();
  };

  const handleOpenTranscription = () => {
    if (!transcription) return;
    const note = doc as Note;
    showNotesTranscriptionModal({
      transcription,
      channelId: note.channelRef as string,
      noteId: note._id,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (editor && stackRef.current) {
      const tocExtension = editor.extensionManager.extensions.find(
        (ext) => ext.name === 'tableOfContents',
      );

      if (tocExtension) {
        tocExtension.options.scrollParent = stackRef.current;
      }
    }
  }, [editor, stackRef.current]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      openedTranscription ||
      !doc?.noteRef ||
      doc?.speakersSelected ||
      transcription?.speakersSelected ||
      !transcription
    )
      return;

    setOpenedTranscription(true);
    handleOpenTranscription();
  }, [openedTranscription, doc, transcription]);

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <RightMenuHeader>
        <Stack direction="row" alignItems="center" gap={2}>
          <HeaderIcon>
            <Iconify icon="tabler:file-text" sx={{ color: 'primary.main' }} />
          </HeaderIcon>
          <Typography variant="h6">
            {doc.noteRef
              ? translate('actions_bar.notes')
              : translate('actions_bar.docs')}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1}>
          <Tooltip title={translate('actions_bar.notes.open_on_docs')}>
            <IconButton onClick={goToDoc}>
              <Iconify icon="tabler:file-import" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}>
            <Iconify icon="tabler:x" />
          </IconButton>
        </Stack>
      </RightMenuHeader>

      <RightMenuContent>
        {!!doc && (
          <>
            <Stack
              direction="row"
              borderBottom="1px solid"
              borderColor="divider"
              alignItems="center"
              justifyContent="space-between"
              px={1}
              py={1.5}
            >
              <WorkspaceShareMembersAvatar
                members={doc.participants}
                size="small"
                max={6}
              />

              <Stack direction="row" alignItems="center" gap={2}>
                {doc.owner === user?._id ? (
                  <ShareDocButton docId={doc._id} />
                ) : (
                  <SharedWithYou />
                )}

                <VerticalDivider height={40} color={theme.palette.divider} />

                <Stack direction="row" alignItems="center" gap={0.5}>
                  <FavoriteDocHeaderAction doc={doc} />

                  <PinDocHeaderAction doc={doc} />

                  <DocOptionsMenu doc={doc} handlePrint={handlePrint} />
                </Stack>
              </Stack>
            </Stack>

            {session && <PastSessionItemHeader session={session} />}

            {(doc as Note).channelRef && (
              <AiTranscription note={doc} onAddToEditor={handleAddToEditor} />
            )}

            <GlobalStyle />
            <Box ref={printRef} sx={{ overflow: 'hidden' }}>
              {doc.cover && <DocCover doc={doc} />}
              <Stack
                direction="row"
                sx={{
                  height: '100%',
                  maxHeight: '100%',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {!doc.noteRef ||
                doc?.speakersSelected ||
                transcription?.speakersSelected ? (
                  <>
                    <DocContentNav />

                    <Stack
                      ref={stackRef}
                      sx={{
                        width: '100%',
                        height: '100%',
                        overflow: 'auto',
                        ml: 6,
                      }}
                    >
                      <DocContainer
                        isFullWidth={docsFullWidth}
                        isContentExpanded={false}
                      >
                        <DocTitle doc={doc} updateDoc={updateDoc} />
                        {editor ? <DocContentEditor editor={editor} /> : null}
                      </DocContainer>

                      <EmptyArea onClick={() => editor.commands.focus()} />
                    </Stack>
                  </>
                ) : (
                  <Stack gap={3} p={3} alignItems="center" flexGrow={1}>
                    <Typography variant="subtitle2">
                      {translate(
                        'communicate.transcription.select_members_warning',
                      )}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleOpenTranscription}
                    >
                      {translate('communicate.transcription.select_members')}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Box>
          </>
        )}
      </RightMenuContent>
    </Stack>
  );
};

export const TranscriptionRightMenuDrawer = ({
  onClose,
}: { onClose(): void }) => {
  const { note, setNote } = useTranscriptionsState();

  useSocketEvent(
    onDocsRefetch(async () => {
      setNote(await DocsRequests.getDocById(note?._id || ''));
    }),
  );

  if (!note) return null;

  return (
    <DocContentContextProvider doc={note}>
      <TranscriptionRightMenuInner onClose={onClose} />
    </DocContentContextProvider>
  );
};
